/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';
import AppBar from '@mui/material/AppBar';

import { Box, ListItemText, Menu, MenuItem, Toolbar } from '@mui/material';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import colorVariables from '../../styles/variables';

import { studioBorderColor } from '../../styles/creative-studio/studio-variables';
import theme from '../../styles/theme';
import { isAdmin } from '../../services/account-service';
import { switchSelectedAccount } from '../../redux/actions/account-actions';
import Account from './account';
import { getSelectedAccount, serviceViewLabels, serviceViews } from '../../helpers/composer-helper';

const useStyles = makeStyles(() => ({
  appBar: {
    background: 'linear-gradient(#fff, #F6F7FA)',
    fontSize: 14,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 22px 0 16px',
    whiteSpace: 'nowrap',
    overflowX: 'scroll',
    scrollbarWidth: 'none',
    '&.MuiPaper-root': {
      boxShadow: 'none',
      borderBottom: `solid 1px ${studioBorderColor}`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0 18px',
    },
  },
  toolBar: {
    height: '36px',
    minHeight: '36px',
    padding: '0 40px',
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      padding: '0 18px',
    },
  },
  switcherContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  switcher: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: 'black',
    padding: '6px 8px',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 500,
  },
  switcherActive: {
    backgroundColor: '#f3f3f3',
  },
  switcherIcon: {
    width: 16,
    height: 16,
    color: '#a2a2a2',
    marginLeft: '-8px',
  },
  itemIcon: {
    width: 18,
    height: 18,
    marginLeft: 12,
    verticalAlign: 'text-top',
  },
  logo: {
    margin: '0px 16px 0 0',
    height: '24px',
  },
  logoContainer: {
    height: 23,
    padding: 6,
  },
  serviceName: {
    fontSize: '14px',
    fontWeight: 400,
    color: colorVariables.composerLinkBlack,
    '&:hover': {
      color: colorVariables.composerLinkHoverPurple,
    },
  },
  accountName: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    color: colorVariables.composerLinkBlack,
    paddingRight: 8,
    '&.admin': {
      '&:hover': {
        color: colorVariables.composerLinkHoverPurple,
      },
    },
  },
  f1: {
    flex: 1,
  },
}));

const ServicesTopNav = ({ selectedService, children }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const userAccount = useSelector((/** @type {Object} */ state) => state.account);
  const selectedAccount = useSelector((state) => getSelectedAccount(state));

  const switcherRef = useRef(null);

  const admin = isAdmin(userAccount);
  const { name: accountName } = selectedAccount;

  const [anchorElement, setAnchorElement] = useState(undefined);

  const selectAccount = () => {
    dispatch(switchSelectedAccount());
    document.location = '/select-account';
  };

  let view;
  if (selectedService === serviceViewLabels.composer) {
    view = serviceViews.composer;
  } else if (selectedService === serviceViewLabels.studio) {
    view = serviceViews.studio;
  } else if (selectedService === serviceViewLabels.dashbaord) {
    view = serviceViews.dashbaord;
  }

  return (
    <AppBar position="relative" classes={{ root: classes.appBar }}>
      <Toolbar disableGutters classes={{ root: classes.toolBar }}>
        <div className={classes.switcherContainer}>
          <a href="/dashboard" className={classes.logoContainer}>
            <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19.5321 2.69066C19.1143 2.44427 18.611 2.43597 18.1858 2.66992L15.2074 4.30591V6.21318L18.5271 4.3897V14.759L7.87291 20.611V10.2418L11.1195 8.4581V6.55083L6.90533 8.86627C6.47012 9.10437 6.2002 9.56065 6.2002 10.0559V21.1412C6.2002 21.6265 6.44936 22.0629 6.86795 22.3092C7.08306 22.4362 7.3206 22.5001 7.55896 22.5001C7.78321 22.5001 8.00829 22.4436 8.21426 22.33L19.4964 16.1328C19.9307 15.8939 20.2007 15.4384 20.2007 14.9432V3.85875C20.2007 3.37343 19.9515 2.93706 19.5329 2.69066H19.5321Z"
                fill="#5A24FB"
              />
              <path
                d="M1.67271 7.74105L12.3269 1.88899V12.2583L9.08033 14.0419V15.9509L13.2953 13.6338C13.7297 13.3948 13.9996 12.9394 13.9996 12.4441V1.35887C13.9996 0.873545 13.7505 0.437171 13.3327 0.190777C12.9149 -0.0556168 12.4116 -0.0639129 11.9856 0.169207L0.7043 6.36555C0.269926 6.60448 0 7.05994 0 7.55521V18.6404C0 19.1258 0.249163 19.5621 0.666926 19.8085C0.882037 19.9355 1.1204 19.9993 1.35877 19.9993C1.58301 19.9993 1.80809 19.9429 2.01407 19.8293L4.99239 18.1933V16.286L1.67271 18.1095V7.74105Z"
                fill="#5A24FB"
              />
            </svg>
          </a>
          <div
            className={classNames(classes.switcher, { [classes.switcherActive]: anchorElement })}
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              setAnchorElement(e.target);
            }}
            ref={switcherRef}
          >
            {selectedService}
            <UnfoldMoreRoundedIcon className={classes.switcherIcon} />
          </div>
        </div>
        <div className={classes.f1} />

        {admin ? (
          <Box
            onClick={selectAccount}
            className={classNames(classes.accountName, 'admin')}
            style={{ cursor: 'pointer' }}
          >
            {accountName}
          </Box>
        ) : (
          <div className={classes.accountName}>{accountName}</div>
        )}
        <Account />
        <Menu
          open={Boolean(anchorElement)}
          onClose={() => {
            setAnchorElement(null);
          }}
          anchorEl={switcherRef.current}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              setAnchorElement(null);
              document.location = '/dashboard';
            }}
            selected={view === serviceViews.dashbaord}
            style={{
              paddingRight: view === serviceViews.dashbaord ? 12 : 16,
              minWidth: 200,
              display: 'flex',
              color: view === serviceViews.dashbaord ? colorVariables.composerPurple : 'black',
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontSize: '14px',
              }}
            >
              Dashboard
            </ListItemText>
            <span style={{ flex: 1 }} />
            {view === serviceViews.dashbaord && <CheckRoundedIcon className={classes.itemIcon} />}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorElement(null);
              document.location = '/composer';
            }}
            selected={view === serviceViews.composer}
            style={{
              paddingRight: view === serviceViews.composer ? 12 : 16,
              minWidth: 200,
              display: 'flex',
              color: view === serviceViews.composer ? colorVariables.composerPurple : 'black',
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontSize: '14px',
              }}
            >
              Stream Composer
            </ListItemText>
            <span style={{ flex: 1 }} />
            {view === serviceViews.composer && <CheckRoundedIcon className={classes.itemIcon} />}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorElement(null);
              document.location = '/studio';
            }}
            selected={view === serviceViews.studio}
            style={{
              paddingRight: view === serviceViews.studio ? 12 : 16,
              minWidth: 200,
              display: 'flex',
              color: view === serviceViews.studio ? colorVariables.composerPurple : 'black',
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontSize: '14px',
              }}
            >
              Creative Studio
            </ListItemText>
            <span style={{ flex: 1 }} />
            {view === serviceViews.studio && <CheckRoundedIcon className={classes.itemIcon} />}
          </MenuItem>
        </Menu>
      </Toolbar>
      {children}
    </AppBar>
  );
};

ServicesTopNav.defaultProps = {
  children: '',
};

ServicesTopNav.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func, PropTypes.string]),
  selectedService: PropTypes.string.isRequired,
};

export default ServicesTopNav;
